import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";
import {
    ArrowUpRight,
    Building2,
    UtensilsCrossed,
    Stethoscope,
    Mail,
    Instagram,
    Facebook,
    Menu,
    X,
    Cpu,
    Network,
    ShieldCheck,
    LineChart,
    Layers,
    Sparkles,
    Check,
    Send,
    Loader2,
} from "lucide-react";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_imperio-robusto/artifacts/2wqthx2k_Logo%20%28500x500%29.png";

const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.85,
            delay: 0.06 * i,
            ease: [0.22, 1, 0.36, 1],
        },
    }),
};

/* -------------------------------------------------------------------------- */
/*                              AMBIENT BACKDROP                              */
/* -------------------------------------------------------------------------- */
const AmbientBackdrop = () => (
    <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[#050505]" />
        <div className="absolute inset-0 tech-grid opacity-60" />
        <div
            className="aurora"
            style={{
                top: "-12%",
                left: "8%",
                width: 520,
                height: 520,
                background: "rgba(0,212,255,0.22)",
            }}
        />
        <div
            className="aurora"
            style={{
                top: "30%",
                right: "-8%",
                width: 480,
                height: 480,
                background: "rgba(212,175,55,0.18)",
            }}
        />
        <div
            className="aurora"
            style={{
                bottom: "-10%",
                left: "30%",
                width: 600,
                height: 600,
                background: "rgba(0,148,184,0.18)",
            }}
        />
    </div>
);

/* -------------------------------------------------------------------------- */
/*                                   HEADER                                   */
/* -------------------------------------------------------------------------- */
const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const nav = [
        { label: "Expertise", href: "#expertise" },
        { label: "Processo", href: "#processo" },
        { label: "Contacto", href: "#contacto" },
    ];

    return (
        <motion.header
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-ink/70 backdrop-blur-xl border-b border-white/5"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-24 md:h-28 flex items-center justify-between">
                <a
                    href="#top"
                    data-testid="logo-link"
                    className="flex items-center gap-5 group"
                >
                    <span className="relative flex items-center justify-center h-[76px] w-[76px] md:h-[84px] md:w-[84px] rounded-full bg-white shadow-[0_0_24px_-2px_rgba(0,212,255,0.55)] transition-shadow duration-500 group-hover:shadow-[0_0_36px_0px_rgba(0,212,255,0.85)]">
                        <span className="absolute -inset-2 rounded-lg bg-electric/20 blur-2xl opacity-60 group-hover:opacity-90 transition-opacity duration-500 pointer-events-none" />
                        <img
                            src={LOGO_URL}
                            alt="Soluções Romanas"
                            className="relative h-14 w-14 md:h-16 md:w-16 object-contain"
                        />
                    </span>
                    <span className="hidden sm:flex flex-col leading-tight">
                        <span className="font-display text-white text-[20px] md:text-[22px] tracking-tight">
                            Soluções Romanas
                        </span>
                        <span className="font-mono text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-electric/80 mt-1">
                            Infraestrutura · Digital
                        </span>
                    </span>
                </a>

                <nav className="hidden md:flex items-center gap-10">
                    {nav.map((n, i) => (
                        <motion.a
                            key={n.href}
                            href={n.href}
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 + i * 0.06, duration: 0.5 }}
                            data-testid={`nav-link-${n.label.toLowerCase()}`}
                            className="text-white/85 hover:text-electric text-[12px] tracking-[0.28em] uppercase font-semibold link-underline transition-colors"
                        >
                            {n.label}
                        </motion.a>
                    ))}
                </nav>

                <a
                    href="#contacto"
                    data-testid="header-cta-button"
                    className="hidden md:inline-flex btn-glass !py-2 !px-3.5 !text-[10px] !tracking-[0.2em] !gap-1.5"
                >
                    Marcar Consulta
                    <ArrowUpRight className="h-3 w-3" />
                </a>

                <button
                    data-testid="mobile-menu-toggle"
                    aria-label="Abrir menu"
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden text-white p-2"
                >
                    {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        data-testid="mobile-menu"
                        className="md:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-t border-white/5"
                    >
                        <div className="px-6 py-8 flex flex-col gap-6">
                            {nav.map((n) => (
                                <a
                                    key={n.href}
                                    href={n.href}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                                    className="text-white text-sm tracking-[0.28em] uppercase"
                                >
                                    {n.label}
                                </a>
                            ))}
                            <a
                                href="#contacto"
                                onClick={() => setOpen(false)}
                                data-testid="mobile-cta-button"
                                className="btn-glass w-full justify-center"
                            >
                                Consulta Técnica
                                <ArrowUpRight className="h-4 w-4" />
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

/* -------------------------------------------------------------------------- */
/*                                    HERO                                    */
/* -------------------------------------------------------------------------- */
const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yShift = useTransform(scrollYProgress, [0, 1], [0, -80]);
    const opacityShift = useTransform(scrollYProgress, [0, 0.8], [1, 0.4]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="relative min-h-[100vh] flex items-center pt-32 md:pt-40 pb-24 overflow-hidden"
        >
            {/* radial center glow */}
            <div className="absolute inset-0 bg-radial-glow opacity-80 pointer-events-none" />

            <motion.div
                style={{ y: yShift, opacity: opacityShift }}
                className="relative max-w-[1400px] w-full mx-auto px-6 md:px-12"
            >
                <div className="grid lg:grid-cols-12 gap-14 items-center">
                    <div className="lg:col-span-7">
                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            animate="show"
                            custom={0}
                            className="flex items-center gap-3 mb-8"
                        >
                            <span className="inline-block w-10 h-px bg-electric" />
                            <span className="eyebrow num-mono">
                                MMXXVI · INFRA-OPS_v3.1
                            </span>
                        </motion.div>

                        <motion.p
                            variants={fadeUp}
                            initial="hidden"
                            animate="show"
                            custom={1}
                            data-testid="hero-slogan"
                            className="font-display text-white/55 text-lg md:text-xl mb-10 max-w-xl leading-relaxed"
                        >
                            “Grandes impérios não se constroem em bases de areia.”
                        </motion.p>

                        <motion.h1
                            variants={fadeUp}
                            initial="hidden"
                            animate="show"
                            custom={2}
                            data-testid="hero-headline"
                            className="font-display text-[44px] sm:text-6xl lg:text-[84px] leading-[0.98] tracking-[-0.03em] text-white max-w-4xl"
                        >
                            Infraestrutura digital,
                            <br />
                            em{" "}
                            <span className="text-gold-gradient">
                                alta densidade
                            </span>
                            .
                        </motion.h1>

                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            animate="show"
                            custom={3}
                            className="h-px w-24 bg-gradient-to-r from-electric to-transparent my-10"
                        />

                        <motion.p
                            variants={fadeUp}
                            initial="hidden"
                            animate="show"
                            custom={4}
                            data-testid="hero-subheadline"
                            className="text-white/70 text-base md:text-lg max-w-xl leading-[1.7] font-light"
                        >
                            Concebemos, implementamos e estabilizamos a stack
                            digital de PMEs portuguesas. Sem pirotecnia. Sem
                            dependências frágeis. Sistemas que escalam,
                            arquitectados para resistir.
                        </motion.p>

                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            animate="show"
                            custom={5}
                            className="mt-12 flex flex-col sm:flex-row gap-5 items-start sm:items-center"
                        >
                            <a
                                href="#contacto"
                                data-testid="hero-primary-cta"
                                className="btn-glass"
                            >
                                Falar com um Especialista
                                <ArrowUpRight className="h-4 w-4" />
                            </a>
                            <a
                                href="#processo"
                                data-testid="hero-secondary-cta"
                                className="text-white/70 hover:text-electric text-[12px] tracking-[0.28em] uppercase font-semibold link-underline"
                            >
                                Ver Processo →
                            </a>
                        </motion.div>

                        {/* status row */}
                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            animate="show"
                            custom={6}
                            className="mt-16 flex flex-wrap gap-x-10 gap-y-5 items-center text-[11px] tracking-[0.28em] uppercase text-white/40 font-mono"
                        >
                            <span className="flex items-center gap-2">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-electric opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-electric" />
                                </span>
                                Sistemas · Operacionais
                            </span>
                            <span>UPTIME · 99.97%</span>
                            <span>PT-PT · LISBOA</span>
                        </motion.div>
                    </div>

                    {/* Right: telemetry panel */}
                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                            delay: 0.5,
                            duration: 0.9,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className="lg:col-span-5 hidden lg:block"
                    >
                        <div className="relative glass glow-border rounded-md p-8">
                            <span className="bracket-tl" />
                            <span className="bracket-tr" />
                            <span className="bracket-bl" />
                            <span className="bracket-br" />

                            <div className="flex items-center justify-between mb-8">
                                <span className="eyebrow">/ Territórios</span>
                                <span className="num-mono text-[10px] text-white/40 tracking-[0.24em]">
                                    03 · ATIVOS
                                </span>
                            </div>

                            <ul className="space-y-1">
                                {[
                                    {
                                        n: "01",
                                        t: "Imobiliárias",
                                        m: "Captura · Autoridade",
                                    },
                                    {
                                        n: "02",
                                        t: "Restauração",
                                        m: "Reservas · Local SEO",
                                    },
                                    {
                                        n: "03",
                                        t: "Clínicas & Estética",
                                        m: "Marcações · Retenção",
                                    },
                                ].map((it) => (
                                    <li
                                        key={it.n}
                                        className="group flex items-center justify-between py-4 border-b border-white/5 last:border-0"
                                    >
                                        <div className="flex items-baseline gap-5">
                                            <span className="num-mono text-electric text-xs tracking-[0.18em]">
                                                {it.n}
                                            </span>
                                            <div>
                                                <div className="font-display text-white text-lg leading-tight">
                                                    {it.t}
                                                </div>
                                                <div className="font-mono text-[10px] tracking-[0.22em] text-white/35 uppercase mt-1">
                                                    {it.m}
                                                </div>
                                            </div>
                                        </div>
                                        <ArrowUpRight className="h-4 w-4 text-white/30 group-hover:text-electric group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-500" />
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between">
                                <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/40">
                                    Ping · Médio
                                </span>
                                <span className="num-mono text-electric text-xs">
                                    14 ms
                                </span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </motion.div>
        </section>
    );
};

/* -------------------------------------------------------------------------- */
/*                          EXPERTISE — BENTO GRID                            */
/* -------------------------------------------------------------------------- */
const Expertise = () => {
    const cards = [
        {
            n: "01",
            Icon: Building2,
            title: "Imobiliárias",
            text: "Páginas de captura cirúrgicas para mediadoras e consultores independentes. Arquitectura técnica orientada à autoridade e à conversão de proprietários.",
            tags: ["Landing", "SEO", "Lead Routing"],
            span: "lg:col-span-7 lg:row-span-2",
            featured: true,
        },
        {
            n: "02",
            Icon: UtensilsCrossed,
            title: "Restauração",
            text: "Motores de reserva proprietários e dominância local. Desacoplamos a operação de plataformas de terceiros.",
            tags: ["Reservas", "Local"],
            span: "lg:col-span-5",
        },
        {
            n: "03",
            Icon: Stethoscope,
            title: "Clínicas & Estética",
            text: "Orquestração de marcações e gestão de base de dados clínica. Agenda saturada, retenção aferida por sistema.",
            tags: ["Agenda", "CRM"],
            span: "lg:col-span-5",
        },
        {
            Icon: Cpu,
            title: "Stack proprietária",
            text: "Sistemas próprios. Sem dependências obscuras.",
            mini: true,
            span: "lg:col-span-3",
        },
        {
            Icon: ShieldCheck,
            title: "Resiliência",
            text: "Backups, failover e telemetria de série.",
            mini: true,
            span: "lg:col-span-4",
        },
        {
            Icon: LineChart,
            title: "Telemetria",
            text: "KPIs em tempo real. Decisão sobre dados.",
            mini: true,
            span: "lg:col-span-5",
        },
    ];

    return (
        <section
            id="expertise"
            data-testid="expertise-section"
            className="relative py-32 md:py-40"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="grid lg:grid-cols-12 gap-10 mb-20"
                >
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="inline-block w-10 h-px bg-electric" />
                            <span className="eyebrow num-mono">
                                / 01 · TERRITÓRIOS
                            </span>
                        </div>
                        <h2
                            data-testid="expertise-title"
                            className="font-display text-white text-5xl lg:text-[64px] leading-[1.02] tracking-[-0.025em]"
                        >
                            Três sectores.
                            <br />
                            <span className="text-electric-gradient">
                                Uma disciplina
                            </span>{" "}
                            técnica.
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                        <p className="text-white/65 text-base md:text-lg leading-[1.7] max-w-xl">
                            Cada sector exige uma arquitectura específica. Não
                            aplicamos templates: desenhamos a infraestrutura
                            necessária para o contexto operacional de cada
                            empresa.
                        </p>
                    </div>
                </motion.div>

                {/* Bento grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 auto-rows-[minmax(220px,auto)]">
                    {cards.map(
                        (
                            { n, Icon, title, text, tags, span, featured, mini },
                            idx,
                        ) => (
                            <motion.article
                                key={title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{
                                    duration: 0.7,
                                    delay: idx * 0.07,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                data-testid={
                                    n
                                        ? `expertise-card-${n}`
                                        : `expertise-mini-card-${idx}`
                                }
                                className={`group relative glass glow-border rounded-md overflow-hidden ${span} ${
                                    featured
                                        ? "p-10 md:p-12"
                                        : mini
                                          ? "p-7"
                                          : "p-9"
                                }`}
                            >
                                <span className="bracket-tl" />
                                <span className="bracket-br" />

                                {/* Number / icon row */}
                                <div className="flex items-start justify-between mb-8">
                                    {n ? (
                                        <span className="num-mono text-electric text-sm tracking-[0.2em]">
                                            {n}
                                        </span>
                                    ) : (
                                        <span className="num-mono text-white/30 text-[10px] tracking-[0.28em] uppercase">
                                            / módulo
                                        </span>
                                    )}
                                    <div className="relative">
                                        <span className="absolute inset-0 rounded-full bg-electric/15 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                        <Icon
                                            className={`relative ${
                                                featured ? "h-7 w-7" : "h-6 w-6"
                                            } text-white/65 group-hover:text-electric transition-colors duration-500`}
                                            strokeWidth={1.25}
                                        />
                                    </div>
                                </div>

                                <h3
                                    className={`font-display text-white leading-tight mb-4 ${
                                        featured
                                            ? "text-[34px]"
                                            : mini
                                              ? "text-xl"
                                              : "text-[26px]"
                                    }`}
                                >
                                    {title}
                                </h3>
                                <p
                                    className={`text-white/65 leading-[1.7] ${
                                        mini ? "text-sm" : "text-[15px]"
                                    }`}
                                >
                                    {text}
                                </p>

                                {tags && (
                                    <div className="absolute bottom-6 left-9 md:left-10 right-9 flex flex-wrap gap-2 pt-6 border-t border-white/5 mt-6">
                                        {tags.map((t) => (
                                            <span
                                                key={t}
                                                className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/50 px-2.5 py-1 border border-white/10 rounded-sm"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </motion.article>
                        ),
                    )}
                </div>
            </div>
        </section>
    );
};

/* -------------------------------------------------------------------------- */
/*                                   PROCESS                                  */
/* -------------------------------------------------------------------------- */
const Process = () => {
    const steps = [
        {
            n: "I",
            Icon: Network,
            title: "Diagnóstico",
            sub: "Auditoria técnica completa",
            text: "Mapeamos pontos de falha, dependências críticas e oportunidades estruturais. Stack actual, performance, captação, conversão.",
        },
        {
            n: "II",
            Icon: Layers,
            title: "Implementação",
            sub: "Construção dos sistemas",
            text: "Desenho à medida. Desenvolvimento, integração e deploy. Pipeline de release controlado, sem janelas de risco.",
        },
        {
            n: "III",
            Icon: Sparkles,
            title: "Consolidação",
            sub: "Estabilização e telemetria",
            text: "Afinação, observabilidade e transferência operacional. A infraestrutura passa a operar autonomamente.",
        },
    ];

    return (
        <section
            id="processo"
            data-testid="process-section"
            className="relative py-32 md:py-40"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8 }}
                    className="grid lg:grid-cols-12 gap-10 mb-20"
                >
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="inline-block w-10 h-px bg-gold" />
                            <span className="eyebrow eyebrow-gold num-mono">
                                / 02 · PROCESSO
                            </span>
                        </div>
                        <h2
                            data-testid="process-title"
                            className="font-display text-white text-5xl lg:text-[64px] leading-[1.02] tracking-[-0.025em]"
                        >
                            Três fases.
                            <br />
                            <span className="text-gold-gradient">
                                Resultado verificável
                            </span>{" "}
                            em cada uma.
                        </h2>
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9 flex items-end">
                        <p className="text-white/65 text-base md:text-lg leading-[1.7]">
                            Sem promessas vazias. Sem dependências
                            obscuras. Cada fase produz um output técnico
                            verificável.
                        </p>
                    </div>
                </motion.div>

                <div className="relative grid md:grid-cols-3 gap-5">
                    {/* connector line */}
                    <div className="hidden md:block absolute left-10 right-10 top-12 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

                    {steps.map((s, i) => (
                        <motion.div
                            key={s.n}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{
                                duration: 0.7,
                                delay: i * 0.12,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            data-testid={`process-step-${i + 1}`}
                            className="relative glass glow-border rounded-md p-9"
                        >
                            <span className="bracket-tl" />
                            <span className="bracket-br" />

                            <div className="flex items-center justify-between mb-8">
                                <span className="relative flex items-center justify-center h-14 w-14 rounded-full border border-gold/40">
                                    <span className="absolute inset-0 rounded-full bg-gold/10 blur-md" />
                                    <span className="relative font-display text-gold text-xl">
                                        {s.n}
                                    </span>
                                </span>
                                <s.Icon
                                    className="h-6 w-6 text-white/45"
                                    strokeWidth={1.25}
                                />
                            </div>

                            <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-electric/80 mb-2">
                                Fase · {s.n}
                            </div>
                            <h3 className="font-display text-white text-[28px] leading-tight mb-1">
                                {s.title}
                            </h3>
                            <p className="text-white/45 text-sm mb-5">
                                {s.sub}
                            </p>
                            <p className="text-white/70 text-[15px] leading-[1.7]">
                                {s.text}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* -------------------------------------------------------------------------- */
/*                                   CONTACT                                  */
/* -------------------------------------------------------------------------- */
const Contact = () => {
    const formRef = useRef(null);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        website: "",
        message: "",
    });
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle"); // idle | sending | success | error

    const update = (k) => (e) => {
        setForm({ ...form, [k]: e.target.value });
        if (errors[k]) setErrors({ ...errors, [k]: false });
    };

    const validate = () => {
        const next = {};
        if (!form.name.trim()) next.name = true;
        if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
            next.email = true;
        if (!form.message.trim()) next.message = true;
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error("Preencha os campos obrigatórios.");
            return;
        }

        const SERVICE = process.env.REACT_APP_EMAILJS_SERVICE_ID;
        const TEMPLATE = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
        const PUBLIC = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

        setStatus("sending");
        try {
            if (SERVICE && TEMPLATE && PUBLIC) {
                await emailjs.send(
                    SERVICE,
                    TEMPLATE,
                    {
                        from_name: form.name,
                        from_email: form.email,
                        phone: form.phone,
                        website: form.website,
                        message: form.message,
                    },
                    { publicKey: PUBLIC },
                );
            } else {
                // Placeholders not yet configured — simulate success
                await new Promise((r) => setTimeout(r, 900));
            }
            setStatus("success");
            toast.success("Pedido registado. Responderemos em breve.");
            setForm({ name: "", email: "", phone: "", website: "", message: "" });
            setTimeout(() => setStatus("idle"), 3500);
        } catch (err) {
            console.error(err);
            setStatus("error");
            toast.error("Falha no envio. Tente novamente.");
            setTimeout(() => setStatus("idle"), 3000);
        }
    };

    return (
        <section
            id="contacto"
            data-testid="contact-section"
            className="relative py-32 md:py-40"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-16">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8 }}
                        className="lg:col-span-5"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <span className="inline-block w-10 h-px bg-electric" />
                            <span className="eyebrow num-mono">
                                / 03 · CONTACTO
                            </span>
                        </div>
                        <h2
                            data-testid="contact-title"
                            className="font-display text-white text-5xl lg:text-[60px] leading-[1.02] tracking-[-0.025em] mb-10"
                        >
                            Resolução técnica
                            <br />
                            de{" "}
                            <span className="text-electric-gradient">
                                problemas digitais
                            </span>
                            .
                        </h2>
                        <p className="text-white/65 text-base md:text-lg leading-[1.7] max-w-lg mb-12">
                            Descreva a situação. Após análise preliminar,
                            agendamos uma reunião técnica para avaliar
                            viabilidade e enquadramento.
                        </p>

                        <div className="space-y-3">
                            <a
                                href="mailto:info@solucoesromanas.pt"
                                data-testid="contact-email-link"
                                className="group flex items-start gap-5 p-5 glass rounded-md glow-border"
                            >
                                <Mail className="h-5 w-5 mt-1 text-electric" strokeWidth={1.5} />
                                <div>
                                    <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-white/40 mb-1.5">
                                        Email
                                    </div>
                                    <div className="font-display text-white text-lg group-hover:text-electric transition-colors">
                                        info@solucoesromanas.pt
                                    </div>
                                </div>
                            </a>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8, delay: 0.15 }}
                        className="lg:col-span-6 lg:col-start-7"
                    >
                        <form
                            ref={formRef}
                            onSubmit={onSubmit}
                            noValidate
                            data-testid="contact-form"
                            className="relative glass-strong rounded-md p-10 md:p-14 overflow-hidden"
                        >
                            <span className="bracket-tl" />
                            <span className="bracket-tr" />
                            <span className="bracket-bl" />
                            <span className="bracket-br" />

                            <div className="flex items-center justify-between mb-10">
                                <span className="eyebrow">
                                    / Formulário Preliminar
                                </span>
                                <span className="font-mono text-[10px] text-white/40 tracking-[0.24em]">
                                    SECURE · TLS
                                </span>
                            </div>

                            {/* Success overlay */}
                            <AnimatePresence>
                                {status === "success" && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.4 }}
                                        className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-ink/85 backdrop-blur-md"
                                        data-testid="contact-success-overlay"
                                    >
                                        <motion.div
                                            initial={{ scale: 0.6, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{
                                                type: "spring",
                                                stiffness: 240,
                                                damping: 16,
                                            }}
                                            className="relative h-20 w-20 rounded-full border border-electric flex items-center justify-center mb-6"
                                        >
                                            <span className="absolute inset-0 rounded-full bg-electric/15 blur-xl" />
                                            <Check
                                                className="relative h-9 w-9 text-electric"
                                                strokeWidth={2}
                                            />
                                        </motion.div>
                                        <div className="font-display text-white text-2xl mb-2">
                                            Pedido transmitido.
                                        </div>
                                        <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/50">
                                            Resposta em 48h úteis
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                                <div className="md:col-span-1">
                                    <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-white/45 mb-2">
                                        Nome
                                    </label>
                                    <input
                                        data-testid="contact-input-name"
                                        type="text"
                                        value={form.name}
                                        onChange={update("name")}
                                        className={`tech-input ${
                                            errors.name ? "is-error" : ""
                                        }`}
                                        placeholder="O seu nome"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-white/45 mb-2">
                                        Email
                                    </label>
                                    <input
                                        data-testid="contact-input-email"
                                        type="email"
                                        value={form.email}
                                        onChange={update("email")}
                                        className={`tech-input ${
                                            errors.email ? "is-error" : ""
                                        }`}
                                        placeholder="endereco@empresa.pt"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-white/45 mb-2">
                                        Telemóvel
                                    </label>
                                    <input
                                        data-testid="contact-input-phone"
                                        type="tel"
                                        name="phone"
                                        id="phone"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        className="tech-input"
                                        placeholder="+351 ··· ··· ···"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-white/45 mb-2">
                                        Website
                                    </label>
                                    <input
                                        data-testid="contact-input-website"
                                        type="text"
                                        value={form.website}
                                        onChange={update("website")}
                                        className="tech-input"
                                        placeholder="www.empresa.pt"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-white/45 mb-2">
                                        Mensagem
                                    </label>
                                    <textarea
                                        data-testid="contact-input-message"
                                        rows={4}
                                        value={form.message}
                                        onChange={update("message")}
                                        className={`tech-input resize-none ${
                                            errors.message ? "is-error" : ""
                                        }`}
                                        placeholder="Descreva a situação actual e os objectivos."
                                    />
                                </div>
                            </div>

                            <div className="mt-12 flex items-center justify-between flex-wrap gap-6">
                                <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/45 max-w-xs">
                                    Resposta · 48h úteis
                                </p>
                                <button
                                    type="submit"
                                    disabled={status === "sending"}
                                    data-testid="contact-submit-button"
                                    className="btn-glass"
                                >
                                    {status === "sending" ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            A Transmitir
                                        </>
                                    ) : (
                                        <>
                                            <Send className="h-4 w-4" />
                                            Enviar Pedido
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

/* -------------------------------------------------------------------------- */
/*                                   FOOTER                                   */
/* -------------------------------------------------------------------------- */
const Footer = () => (
    <footer
        data-testid="site-footer"
        className="relative border-t border-white/5 mt-12"
    >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                <div className="flex items-center gap-4">
                    <img
                        src={LOGO_URL}
                        alt="Soluções Romanas"
                        className="h-12 w-12 object-contain"
                    />
                    <div className="flex flex-col leading-tight">
                        <span className="font-display text-white text-base">
                            Soluções Romanas
                        </span>
                        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-electric/70 mt-0.5">
                            Infraestrutura · Digital
                        </span>
                    </div>
                </div>

                <p
                    data-testid="footer-copyright"
                    className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/40"
                >
                    Soluções Romanas © 2026. Todos os direitos reservados.
                </p>

                <div className="flex items-center gap-3">
                    <a
                        href="https://www.instagram.com/solucoes_romanas/"
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Instagram"
                        data-testid="footer-social-instagram"
                        className="h-10 w-10 rounded-sm border border-white/10 flex items-center justify-center hover:border-electric hover:text-electric text-white/70 transition-colors"
                    >
                        <Instagram className="h-4 w-4" strokeWidth={1.4} />
                    </a>
                    <a
                        href="https://www.facebook.com/profile.php?id=61560179923899"
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Facebook"
                        data-testid="footer-social-facebook"
                        className="h-10 w-10 rounded-sm border border-white/10 flex items-center justify-center hover:border-electric hover:text-electric text-white/70 transition-colors"
                    >
                        <Facebook className="h-4 w-4" strokeWidth={1.4} />
                    </a>
                </div>
            </div>
        </div>
    </footer>
);

/* -------------------------------------------------------------------------- */
/*                                    APP                                     */
/* -------------------------------------------------------------------------- */
function App() {
    return (
        <div className="App relative" data-testid="app-root">
            <Toaster
                position="top-right"
                toastOptions={{
                    style: {
                        background: "rgba(10,10,11,0.92)",
                        color: "#ECECEC",
                        border: "1px solid rgba(0,212,255,0.3)",
                        borderRadius: "2px",
                        fontFamily: "Inter, sans-serif",
                        backdropFilter: "blur(12px)",
                    },
                }}
            />
            <AmbientBackdrop />

            <div className="relative z-10">
                <Header />
                <main>
                    <Hero />
                    <Expertise />
                    <Process />
                    <Contact />
                </main>
                <Footer />
            </div>
        </div>
    );
}

export default App;
